import { useCallback, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { Card, CardColors } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useLocalStorageBool } from '@/utils/localStorage'
import {
  selectCubkProgressData,
  selectShouldStopPolling,
} from './lib/cubkProgress.selectors'
import {
  FETCH_CUBK_PROGRESS_KEY,
  fetchCubkProgress,
} from './lib/cubkProgress.slice'
import { getFetchError } from '@/reducers/fetch'
import {
  selectCubkDisplayPhase,
  selectIsGOWFinalDecision,
  selectGOWStep,
} from './cubk.selectors'
import {
  BankingDisplayState,
  selectBankingDisplayState,
  selectHasConnectedBankAccount,
} from '@/features/banking/banking.selectors'
import { useHasEffectiveActionRequired } from './hooks/useHasEffectiveActionRequired'
import { useFirstLoadResolution } from './hooks/useFirstLoadResolution'
import { usePollingWithBackoff } from './hooks/usePollingWithBackoff'
import { CubkCardCollapsed } from './CubkCardCollapsed'
import { CubkCardExpanded } from './CubkCardExpanded'
import { CubkCardBanking } from './CubkCardBanking'
import {
  CubkCardCollapsedSkeleton,
  CubkProgressPanelSkeleton,
  VisualTimelineSkeleton,
} from './CubkCardSkeleton'
import { CubkCardError } from './CubkCardError'
import { CubkCardState } from './lib/cubkProgress.types'
import {
  CUBK_CARD_EXPANDED_KEY,
  CUBK_COMPLETE_AUTO_EXPANDED_KEY,
} from './cubkCard.constants'
import { updateUserOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { selectCatchUpBookkeepingStatusForCurrentUser } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { markCubkCelebrationSeen } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'

export const CubkCard = () => {
  const dispatch = useAppDispatch()
  const bankingDisplayState = useReselector(selectBankingDisplayState)
  const hasAnyBank = useReselector(selectHasConnectedBankAccount)
  if (!hasAnyBank && bankingDisplayState !== BankingDisplayState.Normal) {
    return <CubkCardBanking displayState={bankingDisplayState} />
  }
  return <CubkCardNormal dispatch={dispatch} />
}

const CubkCardNormal = ({
  dispatch,
}: {
  dispatch: ReturnType<typeof useAppDispatch>
}) => {
  const cubkProgressData = useReselector(selectCubkProgressData)
  const cubkProgressFetchFailed = useReselector(
    getFetchError,
    FETCH_CUBK_PROGRESS_KEY
  )
  const { isInitialLoad, hasInitialLoadError } = useFirstLoadResolution({
    data: cubkProgressData,
    fetchFailed: cubkProgressFetchFailed,
  })

  const shouldStopPolling = useReselector(selectShouldStopPolling)

  const fetchFn = useCallback(async () => {
    await dispatch(fetchCubkProgress({ forceFetch: true }))
    return false
  }, [dispatch])

  usePollingWithBackoff({
    fetchFn,
    shouldStop: shouldStopPolling,
  })

  if (isInitialLoad) {
    return (
      <Card color={CardColors.BLUE_GRADIENT} data-testid="cubk-card">
        <CubkCardCollapsedSkeleton />
        <div className="mt-8">
          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="w-full lg:w-1/2">
              <VisualTimelineSkeleton />
            </div>
            <div className="w-full lg:w-1/2">
              <CubkProgressPanelSkeleton />
            </div>
          </div>
        </div>
      </Card>
    )
  }

  if (hasInitialLoadError) {
    return (
      <Card color={CardColors.BLUE_GRADIENT} data-testid="cubk-card">
        <CubkCardError />
      </Card>
    )
  }

  return <CubkCardLoaded />
}

const CubkCardLoaded = () => {
  const dispatch = useAppDispatch()
  const cardState = useReselector(selectCubkDisplayPhase)
  const currentUser = useReselector(getCurrentUser)
  const gowStep = useReselector(selectGOWStep)
  const isGOWFinalDecision = useReselector(selectIsGOWFinalDecision)

  const { hasEffectiveActionRequired: hasActionRequired } =
    useHasEffectiveActionRequired()
  const {
    isTrue: isExpanded,
    setTrue: expand,
    setFalse: collapse,
  } = useLocalStorageBool(CUBK_CARD_EXPANDED_KEY)

  const cubkStatus = useReselector(selectCatchUpBookkeepingStatusForCurrentUser)
  const dispatchedMarkSeen = useRef(false)

  useEffect(() => {
    if (cardState !== CubkCardState.COMPLETE) return
    if (
      cubkStatus?.celebrationSeenAt !== null &&
      cubkStatus?.celebrationSeenAt !== undefined
    )
      return
    if (dispatchedMarkSeen.current) return
    dispatchedMarkSeen.current = true
    dispatch(markCubkCelebrationSeen())
  }, [cardState, cubkStatus?.celebrationSeenAt, dispatch])

  useEffect(() => {
    if (cardState !== CubkCardState.COMPLETE || !currentUser?.id) return
    if (localStorage.getItem(CUBK_COMPLETE_AUTO_EXPANDED_KEY) !== 'true') {
      localStorage.setItem(CUBK_COMPLETE_AUTO_EXPANDED_KEY, 'true')
      expand()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardState, currentUser?.id])

  useEffect(() => {
    if (hasActionRequired) {
      expand()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActionRequired])

  // Eagerly skip GOW once on celebration render when it's the sole remaining
  // blocker. Silent-failure contract: dispatch is fire-and-forget; PATCH
  // failure recovers via page reload (resets hasEagerSkippedRef on remount).
  // Don't persist this latch — doing so breaks the reload-retry path.
  const hasEagerSkippedRef = useRef(false)
  useEffect(() => {
    if (cardState !== CubkCardState.COMPLETE) return
    if (!isGOWFinalDecision) return
    if (hasEagerSkippedRef.current) return
    if (!gowStep || gowStep.completedAt || gowStep.skippedAt) return
    hasEagerSkippedRef.current = true
    dispatch(
      updateUserOnboardingStep({
        id: gowStep.id,
        updatableFields: { skippedAt: DateTime.now().toISO() },
      })
    )
  }, [cardState, isGOWFinalDecision, gowStep, dispatch])

  const handleToggle = useCallback(() => {
    if (isExpanded) {
      collapse()
    } else {
      expand()
    }
  }, [isExpanded, collapse, expand])

  return (
    <Card color={CardColors.BLUE_GRADIENT} data-testid="cubk-card">
      <CubkCardCollapsed
        cardState={cardState}
        isExpanded={isExpanded}
        onToggle={handleToggle}
      />
      <div
        aria-hidden={!isExpanded}
        data-testid="cubk-expanded-wrapper"
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <CubkCardExpanded />
        </div>
      </div>
    </Card>
  )
}
